/**
 * Funzioni API per i clienti del product-service (Slice A — Forniture).
 * Usano createServiceClient(SERVICE_URLS.product) come da pattern slice 1.
 * Ogni funzione corrisponde a un endpoint REST del product-service.
 */

import { createServiceClient } from '../../api/client.js';
import type {
  Customer,
  CustomerContact,
  CustomerSite,
  CreateContactInput,
  CreateCustomerInput,
  CreateSiteInput,
  Deployment,
  UpdateCustomerInput,
} from './types.js';

/** Client parzialmente applicato per il product-service. */
const productApi = createServiceClient('product');

// ---------------------------------------------------------------------------
// Clienti
// ---------------------------------------------------------------------------

/** Recupera la lista di tutti i clienti. */
export function fetchCustomers(): Promise<Customer[]> {
  return productApi<Customer[]>('/customers');
}

/** Recupera un singolo cliente per ID. Lancia ApiError 404 se assente. */
export function fetchCustomer(id: number): Promise<Customer> {
  return productApi<Customer>(`/customers/${id}`);
}

/** Crea un nuovo cliente. */
export function createCustomer(input: CreateCustomerInput): Promise<Customer> {
  return productApi<Customer>('/customers', { method: 'POST', body: input });
}

/** Aggiorna parzialmente un cliente. */
export function updateCustomer(id: number, input: UpdateCustomerInput): Promise<Customer> {
  return productApi<Customer>(`/customers/${id}`, { method: 'PATCH', body: input });
}

/** Soft-delete di un cliente (risposta 204 → undefined). */
export function deleteCustomer(id: number): Promise<void> {
  return productApi<void>(`/customers/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Forniture del cliente (deployments)
// ---------------------------------------------------------------------------

/**
 * Recupera tutte le consegne (deployments) di un cliente, ordinate per data desc.
 * ?current=true → solo le righe is_current.
 */
export function fetchCustomerDeployments(
  customerId: number,
  currentOnly = false,
): Promise<Deployment[]> {
  const qs = currentOnly ? '?current=true' : '';
  return productApi<Deployment[]>(`/customers/${customerId}/deployments${qs}`);
}

// ---------------------------------------------------------------------------
// Siti del cliente
// ---------------------------------------------------------------------------

/** Recupera i siti di un cliente. */
export function fetchSites(customerId: number): Promise<CustomerSite[]> {
  return productApi<CustomerSite[]>(`/customers/${customerId}/sites`);
}

/** Aggiunge un sito a un cliente. */
export function addSite(customerId: number, input: CreateSiteInput): Promise<CustomerSite> {
  return productApi<CustomerSite>(`/customers/${customerId}/sites`, {
    method: 'POST',
    body: input,
  });
}

/** Rimuove (soft-delete) un sito da un cliente (204 → undefined). */
export function removeSite(customerId: number, siteId: number): Promise<void> {
  return productApi<void>(`/customers/${customerId}/sites/${siteId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Contatti del cliente
// ---------------------------------------------------------------------------

/** Recupera i contatti di un cliente. */
export function fetchContacts(customerId: number): Promise<CustomerContact[]> {
  return productApi<CustomerContact[]>(`/customers/${customerId}/contacts`);
}

/** Aggiunge un contatto a un cliente. */
export function addContact(
  customerId: number,
  input: CreateContactInput,
): Promise<CustomerContact> {
  return productApi<CustomerContact>(`/customers/${customerId}/contacts`, {
    method: 'POST',
    body: input,
  });
}

/** Rimuove (soft-delete) un contatto da un cliente (204 → undefined). */
export function removeContact(customerId: number, contactId: number): Promise<void> {
  return productApi<void>(`/customers/${customerId}/contacts/${contactId}`, { method: 'DELETE' });
}
